import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ChevronDown,
  Copy,
  Pencil,
  Trash2,
  Bell,
  Clock,
  BarChart3,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ManagerNote, GameweekMeta } from "@/hooks/useManagerNotes";
import NoteImage from "./NoteImage";

const CHIP_LABEL: Record<string, string> = {
  wildcard: "Wildcard",
  freehit: "Free Hit",
  bboost: "Bench Boost",
  "3xc": "Triple Captain",
};

interface Props {
  note: ManagerNote;
  gw?: GameweekMeta;
  state: "past" | "current" | "future";
  onEdit: () => void;
  onDelete: () => void;
  onOpenPerformance: () => void;
}

const NoteCard = ({ note, gw, state, onEdit, onDelete, onOpenPerformance }: Props) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(state === "current");

  const shell =
    state === "current"
      ? "border-primary/60 bg-primary/10"
      : state === "past"
        ? "border-border/40 bg-muted/40 opacity-70"
        : "border-border/60 bg-card";

  const copy = async () => {
    const parts = [
      `${gw?.name || `Gameweek ${note.gameweek}`}${note.title ? ` — ${note.title}` : ""}`,
      note.body,
      note.captain ? `Captain: ${note.captain}` : "",
      note.transfers_in ? `In: ${note.transfers_in}` : "",
      note.transfers_out ? `Out: ${note.transfers_out}` : "",
      note.chip_plan ? `Chip: ${CHIP_LABEL[note.chip_plan] ?? note.chip_plan}` : "",
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(parts.join("\n"));
      toast({ title: "Note copied" });
    } catch {
      toast({ title: "Could not copy", variant: "destructive" });
    }
  };

  return (
    <div className={`rounded-lg border ${shell} transition-colors`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left min-h-[44px]"
        aria-expanded={open}
      >
        <span className="shrink-0 rounded-md bg-background/70 border border-border/60 px-1.5 py-0.5 text-[10px] font-bold">
          GW{note.gameweek}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-xs font-semibold truncate">
            {note.title || gw?.name || `Gameweek ${note.gameweek}`}
          </span>
          {gw?.deadline_time && (
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <Clock className="w-3 h-3" />
              {new Date(gw.deadline_time).toLocaleString(undefined, {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
        </span>
        {note.reminder && <Bell className="w-3.5 h-3.5 text-primary shrink-0" />}
        {note.priority === "key" && (
          <Badge variant="secondary" className="text-[9px] px-1.5 py-0 shrink-0">
            Key
          </Badge>
        )}
        {state === "current" && (
          <Badge className="text-[9px] px-1.5 py-0 shrink-0">Now</Badge>
        )}
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          {note.body && (
            <p className="text-xs whitespace-pre-wrap break-words text-foreground/90">{note.body}</p>
          )}

          {(note.captain || note.transfers_in || note.transfers_out || note.chip_plan) && (
            <div className="flex flex-wrap gap-1.5">
              {note.chip_plan && (
                <Badge variant="outline" className="text-[10px]">
                  {CHIP_LABEL[note.chip_plan] ?? note.chip_plan}
                </Badge>
              )}
              {note.captain && (
                <Badge variant="outline" className="text-[10px]">C: {note.captain}</Badge>
              )}
              {note.transfers_in && (
                <Badge variant="outline" className="text-[10px]">In: {note.transfers_in}</Badge>
              )}
              {note.transfers_out && (
                <Badge variant="outline" className="text-[10px]">Out: {note.transfers_out}</Badge>
              )}
            </div>
          )}

          {note.image_url && (
            <NoteImage path={note.image_url} className="rounded-md max-h-40 w-auto object-cover" />
          )}

          <div className="flex flex-wrap gap-1.5 pt-1">
            <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={onEdit}>
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={copy}>
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={onOpenPerformance}>
              <BarChart3 className="w-3 h-3 mr-1" /> Performance
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-[11px] text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoteCard;
